import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  Card,
  CardContent,
} from '@mui/material'
import Close from '@mui/icons-material/Close'
import Business from '@mui/icons-material/Business'
import Add from '@mui/icons-material/Add'
import CheckCircle from '@mui/icons-material/CheckCircle'
import { useNavigate, useParams } from 'react-router-dom'
import { useContext, useMemo } from 'react'
import { ClinicDataContext } from 'src/context/ClinicDataContext'
import { ROLE_TYPES } from 'src/consumer/constants'
import styled from 'styled-components'

// Styled components
const DrawerHeader = styled(Box)`
  padding: 24px;
  border-bottom: 1px solid rgba(116, 80, 170, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, rgba(116, 80, 170, 0.05) 0%, rgba(116, 80, 170, 0.02) 100%);
`

const DrawerTitle = styled(Typography)`
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`

const DrawerContent = styled(Box)`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`

const BrandCard = styled(Card)`
  border-radius: 16px;
  border: 2px solid ${(props) => (props.selected ? 'rgba(116, 80, 170, 0.4)' : 'rgba(116, 80, 170, 0.1)')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
  cursor: pointer;
  overflow: hidden;
  position: relative;
  height: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7450aa 0%, #5a3d88 100%);
    opacity: ${(props) => (props.selected ? 1 : 0)};
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: rgba(116, 80, 170, 0.4);
    box-shadow: 0 8px 24px rgba(116, 80, 170, 0.2);
    transform: translateY(-4px);

    &::before {
      opacity: 1;
    }
  }
`

const BrandCardContent = styled(CardContent)`
  padding: 24px !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
`

const BrandIcon = styled(Box)`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #7450aa 0%, #5a3d88 100%);
  color: white;
  margin-bottom: 16px;
  box-shadow: 0 4px 12px rgba(116, 80, 170, 0.3);
  position: relative;
`

const SelectedBadge = styled(Box)`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
  border: 2px solid white;
`

const BrandName = styled(Typography)`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 4px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`

const BrandShortName = styled(Typography)`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 12px;
`

const AddBrandCard = styled(Card)`
  border-radius: 16px;
  border: 2px dashed rgba(116, 80, 170, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, rgba(116, 80, 170, 0.05) 0%, rgba(116, 80, 170, 0.02) 100%);
  cursor: pointer;
  height: 100%;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: rgba(116, 80, 170, 0.5);
    background: linear-gradient(135deg, rgba(116, 80, 170, 0.1) 0%, rgba(116, 80, 170, 0.05) 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(116, 80, 170, 0.15);
  }
`

const AddBrandContent = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px;
`

const AddIcon = styled(Box)`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #7450aa 0%, #5a3d88 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(116, 80, 170, 0.3);
`

const EmptyState = styled(Box)`
  text-align: center;
  padding: 48px 24px;
  color: #9ca3af;
`

export default function BrandSwitcherDrawer({ open, onClose }) {
  const navigate = useNavigate()
  const { clinicBrandId } = useParams()
  const { chosenRoleFlow, ownerData } = useContext(ClinicDataContext)

  // Get clinic brands list
  const clinicBrands = useMemo(() => {
    if (chosenRoleFlow === ROLE_TYPES.roleClinicOwner) {
      return ownerData?.associatedClinicBrandsData || []
    }
    return []
  }, [chosenRoleFlow, ownerData?.associatedClinicBrandsData])

  const handleBrandSelect = (brandId) => {
    navigate(`/clinic-home/${brandId}`)
    onClose()
  }

  const handleAddNewBrand = () => {
    navigate('/clinic-home/new-brand-onboarding')
    onClose()
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 480 },
          maxWidth: '100%',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <DrawerHeader>
          <DrawerTitle>Switch Clinic Brand</DrawerTitle>
          <IconButton
            onClick={onClose}
            sx={{
              color: '#6b7280',
              '&:hover': {
                background: 'rgba(116, 80, 170, 0.08)',
                color: '#7450aa',
              },
            }}
          >
            <Close />
          </IconButton>
        </DrawerHeader>

        <DrawerContent>
          {clinicBrands.length === 0 ? (
            <EmptyState>
              <Business sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                No clinic brands found
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddNewBrand}
                sx={{
                  background: 'linear-gradient(135deg, #7450aa 0%, #5a3d88 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a3d88 0%, #4a2f6f 100%)',
                  },
                }}
              >
                Add Your First Brand
              </Button>
            </EmptyState>
          ) : (
            <Grid container spacing={2}>
              {clinicBrands.map((brand) => {
                const isSelected = brand.id === clinicBrandId
                const brandName = brand.brandName || brand.title || brand.clinicBrandName || 'Unnamed Brand'
                const brandShortName = brand.brandShortName

                return (
                  <Grid item xs={6} key={brand.id}>
                    <BrandCard
                      selected={isSelected}
                      onClick={() => handleBrandSelect(brand.id)}
                    >
                      <BrandCardContent>
                        <BrandIcon>
                          <Business sx={{ fontSize: 32 }} />
                          {isSelected && (
                            <SelectedBadge>
                              <CheckCircle sx={{ fontSize: 14, color: 'white' }} />
                            </SelectedBadge>
                          )}
                        </BrandIcon>
                        <BrandName>{brandName}</BrandName>
                        {brandShortName && (
                          <BrandShortName>{brandShortName}</BrandShortName>
                        )}
                      </BrandCardContent>
                    </BrandCard>
                  </Grid>
                )
              })}

              {/* Add New Brand Card */}
              <Grid item xs={6}>
                <AddBrandCard onClick={handleAddNewBrand}>
                  <AddBrandContent>
                    <AddIcon>
                      <Add sx={{ fontSize: 24 }} />
                    </AddIcon>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: '#7450aa',
                        fontSize: '14px',
                      }}
                    >
                      Add New Brand
                    </Typography>
                  </AddBrandContent>
                </AddBrandCard>
              </Grid>
            </Grid>
          )}
        </DrawerContent>
      </Box>
    </Drawer>
  )
}

